import React, { useState, useEffect } from 'react';
import { GroupChatModel, CoachModel } from '../../types/group';
import { GroupsService } from '../../services/groupsService';
import Modal from '../../Common/Components/Modal';
import { toast } from 'react-toastify';

interface EditGroupDialogProps {
  group: GroupChatModel;
  onClose: () => void;
  onSave: (updatedGroup: GroupChatModel) => Promise<void>;
  show: boolean;
  coaches: CoachModel[];
}

const EditGroupDialog: React.FC<EditGroupDialogProps> = ({
  group,
  onClose,
  onSave,
  show,
  coaches,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description);
      setStatus(group.status);
      setPreviewUrl(group.photoUrl);
    }
  }, [group]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      let photoUrl = group.photoUrl;

      if (selectedFile) {
        photoUrl = await GroupsService.uploadGroupImage(selectedFile, group.id);
      }

      const updatedGroup: GroupChatModel = {
        ...group,
        name: name.trim(),
        description: description.trim(),
        photoUrl,
        status,
      };

      await onSave(updatedGroup);
      handleClose();
    } catch (error) {
      console.error('Error updating group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onClose} id="editGroupModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Edit Group</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Group Image</label>
              <div className="border-2 border-dashed border-slate-300 dark:border-zink-500 rounded-lg p-4 text-center">
                {previewUrl ? (
                  <div className="space-y-2">
                    <img
                      src={previewUrl}
                      alt="Group preview"
                      className="w-20 h-20 rounded-full object-cover mx-auto"
                    />
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="edit-group-image"
                      />
                      <label
                        htmlFor="edit-group-image"
                        className="cursor-pointer text-custom-500 hover:text-custom-600 text-sm"
                      >
                        Change image
                      </label>
                    </div>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="edit-group-image"
                    />
                    <label
                      htmlFor="edit-group-image"
                      className="cursor-pointer text-custom-500 hover:text-custom-600"
                    >
                      <div className="text-4xl mb-2">📷</div>
                      <p className="text-sm">Click to upload group image</p>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Group Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-zink-500 rounded-md bg-white dark:bg-zink-600 text-slate-900 dark:text-zink-100 focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500"
                placeholder="Enter group name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 dark:border-zink-500 rounded-md bg-white dark:bg-zink-600 text-slate-900 dark:text-zink-100 focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500"
                placeholder="Enter group description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Group Status</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="active"
                    checked={status === 'active'}
                    onChange={(e) => setStatus(e.target.value)}
                    className="mr-2"
                  />
                  Active
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="locked"
                    checked={status === 'locked'}
                    onChange={(e) => setStatus(e.target.value)}
                    className="mr-2"
                  />
                  Locked
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Moderators</label>
              <div className="px-3 py-2 bg-slate-100 dark:bg-zink-600 rounded-md text-slate-600 dark:text-zink-300">
                {group.moderators && group.moderators.length > 0
                  ? group.moderators
                      .map(modId => coaches.find(c => c.id === modId)?.fullName || modId)
                      .join(', ')
                  : 'No moderators assigned'}
              </div>
            </div>
            </div>
  
          </Modal.Body>
          <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-slate-600 dark:text-zink-300 border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-4 py-2 bg-custom-500 text-white rounded-md hover:bg-custom-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              Update Group
            </button>
          </Modal.Footer>
        </Modal>
      );
};

export default EditGroupDialog;